<!-- 字典组件 -->
<template>
  <template v-if="type === 'text'">
    <span v-for="item in valueData" :key="item.dictDataCode">
      {{ item.dictDataName }}
    </span>
  </template>
  <template v-else-if="type === 'tag'">
    <el-tag
      v-for="item in valueData"
      :key="item.dictDataCode"
      :disable-transitions="true"
      size="small"
    >
      {{ item.dictDataName }}
    </el-tag>
  </template>
  <el-radio-group
    v-else-if="type === 'radio'"
    :disabled="disabled"
    @change="change"
    v-model="model as SingleValue"
  >
    <el-radio
      v-for="item in data"
      :key="item.dictDataCode"
      :value="item.dictDataCode"
      :label="item.dictDataName"
    />
  </el-radio-group>
  <el-checkbox-group
    v-else-if="type === 'checkbox'"
    :disabled="disabled"
    @change="change"
    v-model="model as any"
  >
    <el-checkbox
      v-for="item in data"
      :key="item.dictDataCode"
      :value="item.dictDataCode"
      :label="item.dictDataName"
    />
  </el-checkbox-group>
  <el-select
    v-else
    v-model="model"
    :clearable="clearable"
    :disabled="disabled"
    :placeholder="placeholder"
    :multiple="type === 'multipleSelect'"
    :teleported="teleported"
    :filterable="filterable"
    @change="change"
    class="ele-fluid"
  >
    <el-option
      v-for="item in data"
      :key="item.dictDataCode"
      :value="item.dictDataCode!"
      :label="!labelFn ? item.dictDataName : labelFn(item)"
    />
  </el-select>
</template>

<script lang="ts" setup>
  import { computed, toRaw } from 'vue';
  import { useDictData } from '@/utils/use-dict-data';
  import type { TenantDictData } from '@/api/bsl/tenant/dict/types';

  defineOptions({ name: 'DictData' });

  type SingleValue = string | number | boolean;
  type MultipleValue = Array<SingleValue>;
  type TypeValue =
    | 'text'
    | 'tag'
    | 'radio'
    | 'select'
    | 'checkbox'
    | 'multipleSelect';

  const props = withDefaults(
    defineProps<{
      /** 字典类型 */
      code: string;
      /** 组件类型 */
      type?: TypeValue;
      /** 是否禁用 */
      disabled?: boolean;
      /** 提示文本 */
      placeholder?: string;
      /** select是否可清除 */
      clearable?: boolean;
      /** select是否可搜索 */
      filterable?: boolean;
      /** select的下拉是否插入到body下 */
      teleported?: boolean;
      /** 显示 */
      labelFn?: (item: any) => string;
      /** 获取字典数据 */
      dictDatasFn?: () => Partial<TenantDictData>[];
    }>(),
    {
      clearable: true,
      teleported: true
    }
  );

  const emit = defineEmits<{
    (e: 'change', value: any): void;
  }>();

  const change = (value: any) => {
    if (typeof value === 'string') {
      const temp = data.value.find((d) => d.dictDataCode === value);
      if (temp != null) {
        emit('change', toRaw(temp));
        return;
      }
    }
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const temp = data.value.find((d) => d.dictDataCode === value[i]);
        if (temp != null) {
          emit('change', toRaw(temp));
          return;
        }
      }
    }
  };

  /** 字典值 */
  const model = defineModel<SingleValue | MultipleValue>({
    type: [String, Number, Boolean, Array]
  });

  /** 字典数据 */
  const [data] = props.dictDatasFn
    ? [computed(() => props.dictDatasFn!())]
    : useDictData([props.code]);

  /** 绑定值对应的字典数据 */
  const valueData = computed<Partial<TenantDictData>[]>(() => {
    const result: Partial<TenantDictData>[] = [];
    const val = model.value;
    if (val == null || val === '') {
      return result;
    }
    const values: MultipleValue = Array.isArray(val) ? val : [val];
    values.forEach((v) => {
      const temp = data.value.find((d) => d.dictDataCode == v);
      if (temp != null) {
        result.push(temp);
      } else {
        result.push({ dictDataCode: v as string, dictDataName: v as string });
      }
    });
    return result;
  });
</script>
